import { useCallback, useEffect, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RefreshCw, Plus, LogOut, Globe2, Users, Timer, CalendarOff, Receipt, Wallet, Headset, Package, Archive, FileText, UserCog, KeyRound, SlidersHorizontal } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AreaCard from "@/components/master/AreaCard";
import AreaFormDialog from "@/components/master/AreaFormDialog";
import AreaAdminDialog from "@/components/master/AreaAdminDialog";
import MasterStatCard from "@/components/master/MasterStatCard";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import KeuanganSummaryCard from "@/components/summary/KeuanganSummaryCard";
import PekerjaCharts from "@/components/summary/PekerjaCharts";
import useChartPref from "@/hooks/useChartPref";
import PensiunReminderCard from "@/components/summary/PensiunReminderCard";
import BirthdayCard from "@/components/summary/BirthdayCard";
import MasterLemburCard from "@/components/master/MasterLemburCard";
import MasterAdminDialog from "@/components/master/MasterAdminDialog";

const fmtRp = (n) => `Rp ${Math.round(n || 0).toLocaleString("id-ID")}`;

// Halaman Master Admin: tampilan seperti Beranda area, tetapi angka = TOTAL seluruh area. Detail per area lewat "Masuk ke Area".
export default function MasterView() {
  const { user, logout, setActiveArea } = useAuth();
  const [areas, setAreas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adminTarget, setAdminTarget] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const viewer = !!user?.master_viewer;
  const [beranda, setBeranda] = useState(null);
  const [fArea, setFArea] = useState("all");
  const [berandaLoading, setBerandaLoading] = useState(false);
  const [tLokasi, setTLokasi] = useChartPref("master-lokasi");
  const [tJabatan, setTJabatan] = useChartPref("master-jabatan");

  const loadBeranda = useCallback(async (kode) => {
    setBerandaLoading(true);
    try { const b = await api.get("/master/beranda", { params: kode && kode !== "all" ? { area: kode } : {} }); setBeranda(b.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat overview"); }
    finally { setBerandaLoading(false); }
  }, []);
  useEffect(() => { loadBeranda(fArea); }, [fArea, loadBeranda]);

  const loadAreas = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/master/summary"); setAreas(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal memuat ringkasan area"); setAreas([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadAreas(); }, [loadAreas]);
  const load = () => { loadAreas(); loadBeranda(fArea); };

  const masuk = (a) => { setActiveArea(a.kode); toast.success(`Masuk ke area ${a.nama}`); };
  const toggleAktif = async (a) => {
    try { await api.put(`/master/areas/${a.kode}`, { aktif: !a.aktif }); toast.success(a.aktif ? `Area ${a.kode} dinonaktifkan` : `Area ${a.kode} diaktifkan`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah status area"); }
  };
  const aktif = (areas || []).filter((a) => a.aktif);
  const total = (k) => aktif.reduce((s, a) => s + (a.ringkasan?.[k] || 0), 0);
  const v = (k) => (areas ? total(k).toLocaleString("id-ID") : "–");

  return (
    <div className="min-h-screen w-full bg-slate-50" data-testid="master-view">
      <header className="h-16 bg-white/70 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center gap-3">
          <img src="/logo-patra.png" alt="PATRA Facility Management" className="h-10 w-auto" data-testid="master-logo" />
          <div className="min-w-0 ml-1">
            <h1 className="text-lg font-bold text-slate-900 tracking-tight leading-tight flex items-center gap-1.5"><Globe2 className="h-4 w-4 text-amber-500" />{viewer ? "Master Viewer" : "Master Admin"}</h1>
            <p className="text-[11px] text-slate-500 truncate">{user?.name} · {user?.nomor_pegawai} · total {aktif.length} area aktif{viewer ? " · hanya lihat" : ""}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-9 gap-1.5 border-slate-200" data-testid="master-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}<span className="hidden sm:inline">Muat Ulang</span>
            </Button>
            {!viewer && <>
              <Button size="sm" onClick={() => { setEditing(null); setFormOpen(true); }} className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="master-add-area">
                <Plus className="h-4 w-4" />Tambah Area
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdminOpen(true)} className="h-9 gap-1.5 border-slate-200" data-testid="master-kelola-admin">
                <UserCog className="h-4 w-4" /><span className="hidden sm:inline">Kelola Admin</span>
              </Button>
            </>}
            <Button size="sm" variant="outline" onClick={() => setPwOpen(true)} title="Ubah kata sandi" className="h-9 w-9 p-0 border-slate-200" data-testid="master-change-password"><KeyRound className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={logout} className="h-9 gap-1.5 border-slate-200 text-rose-600 hover:bg-rose-50" data-testid="master-logout"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">Keluar</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-8">
        <section className="space-y-6" data-testid="master-overview">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <h2 className="text-base md:text-lg font-bold text-slate-900">{fArea === "all" ? "Overview Gabungan Semua Area" : `Overview Area ${aktif.find((a) => a.kode === fArea)?.nama || fArea}`}</h2>
              <p className="text-xs text-slate-500">{fArea === "all" ? `Tampilan sama seperti Beranda tiap area, dengan angka total dari ${beranda?.areas?.length ?? aktif.length} area aktif.` : "Data hanya dari area terpilih — tanpa perlu masuk ke area."}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600"><SlidersHorizontal className="h-4 w-4 text-blue-600" />Saring area:</div>
              <Select value={fArea} onValueChange={setFArea}>
                <SelectTrigger className="h-9 w-56" data-testid="master-filter-area"><SelectValue placeholder="Semua Area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Area (gabungan)</SelectItem>
                  {aktif.map((a) => <SelectItem key={a.kode} value={a.kode} data-testid={`master-filter-area-${a.kode}`}>{a.nama}</SelectItem>)}
                </SelectContent>
              </Select>
              {fArea !== "all" && <Button variant="ghost" size="sm" onClick={() => setFArea("all")} className="h-9 text-slate-500 hover:text-rose-600" data-testid="master-filter-area-reset">Reset</Button>}
              {berandaLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" data-testid="master-overview-loading" />}
            </div>
          </div>
          {beranda === null ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div> : (<div className={`space-y-6 transition-opacity ${berandaLoading ? "opacity-60" : ""}`}>
            <KeuanganSummaryCard kontrakData={beranda.kontrak} />
            <PekerjaCharts data={beranda.summary} tLokasi={tLokasi} setTLokasi={setTLokasi} tJabatan={tJabatan} setTJabatan={setTJabatan} />
            <MasterLemburCard area={fArea} />
            <PensiunReminderCard reminders={beranda.summary.pensiun_reminders} />
            <BirthdayCard birthdays={beranda.summary.birthdays_this_month} />
          </div>)}
        </section>

        <section className="space-y-3" data-testid="master-totals">
          <div>
            <h2 className="text-base md:text-lg font-bold text-slate-900">Ringkasan Operasional</h2>
            <p className="text-xs text-slate-500">Total gabungan dari {aktif.length} area aktif — dihitung ringan tanpa memuat detail.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            <MasterStatCard icon={Users} label="Pekerja Aktif" value={v("pekerja")} sub={`${v("pensiun")} pensiun`} tone="blue" testid="master-total-pekerja" />
            <MasterStatCard icon={Timer} label="SPL Menunggu" value={v("spl_menunggu")} sub={`${v("spl_bulan_ini")} disetujui bulan ini`} tone={total("spl_menunggu") > 0 ? "amber" : "green"} testid="master-total-spl" />
            <MasterStatCard icon={CalendarOff} label="Cuti Menunggu" value={v("cuti_menunggu")} sub={`${v("cuti_bulan_ini")} cuti bulan ini`} tone={total("cuti_menunggu") > 0 ? "amber" : "green"} testid="master-total-cuti" />
            <MasterStatCard icon={Receipt} label="Tagihan Belum Bayar" value={v("tagihan_belum_bayar")} sub={areas ? fmtRp(total("tagihan_nilai")) : ""} tone={total("tagihan_belum_bayar") > 0 ? "red" : "green"} testid="master-total-tagihan" />
            <MasterStatCard icon={Wallet} label="Kontrak Aktif" value={v("kontrak_aktif")} sub={`${v("kontrak_akan_habis")} habis ≤ 60 hari`} tone={total("kontrak_akan_habis") > 0 ? "amber" : "blue"} testid="master-total-kontrak" />
            <MasterStatCard icon={Headset} label="Helpdesk Terbuka" value={v("helpdesk_terbuka")} tone={total("helpdesk_terbuka") > 0 ? "amber" : "green"} testid="master-total-helpdesk" />
            <MasterStatCard icon={FileText} label="Berita Acara" value={v("ba")} sub={`${v("aset")} aset tercatat`} tone="blue" testid="master-total-ba" />
            <MasterStatCard icon={Package} label="Barang Consumable" value={v("barang")} sub={`${v("akun")} akun pengguna`} tone="blue" testid="master-total-barang" />
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-end justify-between gap-3">
            <div>
              <h2 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2"><Archive className="h-4 w-4 text-slate-400" />Per Area</h2>
              <p className="text-xs text-slate-500">Klik <b>Masuk ke Area</b> untuk membuka dashboard lengkap area tersebut. Ikon <UserCog className="inline h-3 w-3" /> untuk buat/reset admin area.</p>
            </div>
          </div>
          {areas === null ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
            : areas.length === 0 ? <p className="text-slate-400 text-sm">Belum ada area.</p>
            : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="master-area-grid">
                {areas.map((a) => <AreaCard key={a.kode} area={a} readOnly={viewer} onMasuk={() => masuk(a)} onEdit={() => { setEditing(a); setFormOpen(true); }} onAdmin={() => setAdminTarget(a)} onToggleAktif={() => toggleAktif(a)} />)}
              </div>}
        </section>
      </main>

      <AreaFormDialog open={formOpen} onOpenChange={setFormOpen} area={editing} onSaved={load} />
      <AreaAdminDialog area={adminTarget} onOpenChange={(o) => !o && setAdminTarget(null)} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <MasterAdminDialog open={adminOpen} onOpenChange={setAdminOpen} />
    </div>
  );
}
